import {useCallback, useMemo, useRef, useState} from 'react';
import {getNestedValue} from '../utils/formatters';

function applyRows(reports, rows) {
    if (!rows.length) return reports;

    return reports.filter((report) =>
        rows.every((row) => {
            if (!row.values?.length) return true;
            const value = String(getNestedValue(report, row.propertyKey) ?? '').trim();
            const match = row.values.some((v) => v === value);
            return row.operator === '!=' ? !match : match;
        }),
    );
}

/**
 * Client-side filter state for products listing pages.
 * Filters apply automatically on every value change.
 * ponytail: no store, no URL — pure local state is enough for a listing page filter.
 *
 * @param {object[]} reports
 * @param {string[]} [defaultKeys] - property keys that are pre-added and cannot be removed.
 * @param {object[]} [baselineRows] - when provided, rows reset to this value whenever it changes
 *   (identity comparison via ref). Drives the "switch filter → rows reset" behaviour without
 *   a separate applyFilterRows call from the parent.
 */
export function useProductsFilters(reports, defaultKeys = [], baselineRows) {
    const defaultKeysSet = useMemo(() => new Set(defaultKeys), [defaultKeys]);

    const [rows, setRows] = useState(() =>
        baselineRows ?? defaultKeys.map((key) => ({propertyKey: key, operator: '=', values: []})),
    );

    // Keep rows in sync with baselineRows when the active filter changes.
    // Using a ref so we only reset on actual baseline identity changes, not on every render.
    // ponytail: caller MUST pass a memoized (stable) array; an inline literal triggers a reset on
    // every render and will infinite-loop. Upgrade path: switch to useEffect if callers can't
    // guarantee stability.
    if (process.env.NODE_ENV !== 'production' && baselineRows !== undefined && !Array.isArray(baselineRows)) {
        console.warn('useProductsFilters: baselineRows must be an array');
    }
    const prevBaselineRef = useRef(baselineRows);
    if (baselineRows !== undefined && baselineRows !== prevBaselineRef.current) {
        prevBaselineRef.current = baselineRows;
        // ponytail: setState-during-render is the React-recommended pattern for derived state reset;
        // React re-renders immediately with the new value, skipping the stale render entirely.
        setRows(baselineRows);
    }

    const filteredReports = useMemo(() => applyRows(reports, rows), [reports, rows]);

    const onToggleMoreFilter = useCallback((propertyKey) => {
        setRows((prev) => {
            if (prev.some((r) => r.propertyKey === propertyKey)) {
                if (defaultKeysSet.has(propertyKey)) return prev;
                return prev.filter((r) => r.propertyKey !== propertyKey);
            }
            return [...prev, {propertyKey, operator: '=', values: []}];
        });
    }, [defaultKeysSet]);

    const onRemoveFilter = useCallback((propertyKey) => {
        if (defaultKeysSet.has(propertyKey)) return;
        setRows((prev) => prev.filter((r) => r.propertyKey !== propertyKey));
    }, [defaultKeysSet]);

    const onOperatorChange = useCallback((propertyKey, operator) => {
        setRows((prev) => prev.map((r) => r.propertyKey === propertyKey ? {...r, operator} : r));
    }, []);

    const onToggleValue = useCallback((propertyKey, value) => {
        setRows((prev) => prev.map((r) => {
            if (r.propertyKey !== propertyKey) return r;
            const next = r.values.includes(value)
                ? r.values.filter((v) => v !== value)
                : [...r.values, value];
            return {...r, values: next};
        }));
    }, []);

    const onClearValues = useCallback((propertyKey) => {
        setRows((prev) => prev.map((r) => r.propertyKey === propertyKey ? {...r, values: []} : r));
    }, []);

    const onReset = useCallback(() => {
        setRows(defaultKeys.map((key) => ({propertyKey: key, operator: '=', values: []})));
    }, [defaultKeys]);

    /** Replace the full rows state with the given rows (e.g. to apply a saved filter). */
    const applyFilterRows = useCallback((newRows) => {
        setRows(newRows);
    }, []);

    return {
        rows,
        defaultKeysSet,
        filteredReports,
        onToggleMoreFilter,
        onRemoveFilter,
        onOperatorChange,
        onToggleValue,
        onClearValues,
        onReset,
        applyFilterRows,
    };
}
